/* Truth Scriptures: cart (Ahmed Development)
   <cart-drawer> keeps the interface Dawn's product-form.js expects
   (getSectionsToRender, renderContents, setActiveElement, open), so the
   base theme's product form and dynamic checkout are reused unchanged.
   Adds: line updates, "Switch to Combo", card add-to-cart, quick add. */
(() => {
  const T = window.tsTheme || { strings: {} };
  const DRAWER_SECTION = 'ts-cart-drawer';
  const BUBBLE_SECTION = 'cart-icon-bubble';

  const formatMoney = (cents) => {
    const fmt = T.moneyFormat || '{{amount}}';
    const value = (Number(cents) / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`)
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`);
  };

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  const sectionsForPage = () => {
    const ids = [DRAWER_SECTION, BUBBLE_SECTION];
    document.querySelectorAll('[data-ts-cart-page]').forEach((el) => ids.push(el.dataset.sectionId));
    return ids;
  };

  const renderSections = (sections) => {
    if (!sections) return;
    if (sections[BUBBLE_SECTION]) {
      const bubble = document.getElementById('cart-icon-bubble');
      const src = parse(sections[BUBBLE_SECTION]).querySelector('.shopify-section');
      if (bubble && src) bubble.innerHTML = src.innerHTML;
    }
    if (sections[DRAWER_SECTION]) {
      const next = parse(sections[DRAWER_SECTION]).querySelector('[data-ts-cart-inner]');
      const current = document.querySelector('#CartDrawer [data-ts-cart-inner]');
      if (next && current) current.replaceWith(next);
    }
    document.querySelectorAll('[data-ts-cart-page]').forEach((el) => {
      const html = sections[el.dataset.sectionId];
      if (!html) return;
      const next = parse(html).querySelector('[data-ts-cart-inner]');
      const current = el.querySelector('[data-ts-cart-inner]');
      if (next && current) current.replaceWith(next);
    });
    const countSrc = document.querySelector('#CartDrawer [data-ts-item-count]');
    if (countSrc) {
      const n = Number(countSrc.dataset.tsItemCount) || 0;
      const one = T.strings.itemOne || '1 item';
      const other = (T.strings.itemOther || '2 items').replace('2', String(n));
      document.querySelectorAll('[data-ts-cart-pill]').forEach((el) => { el.textContent = n === 1 ? one : other; el.hidden = n === 0; });
    }
    document.querySelectorAll('[data-ts-cart-count]').forEach((el) => {
      const src = document.querySelector('#CartDrawer [data-ts-item-count]');
      if (src) el.textContent = src.dataset.tsItemCount;
    });
    window.tsInitAccordions?.(document.getElementById('CartDrawer'));
    updateShippingBars();
    document.dispatchEvent(new CustomEvent('ts:cart-updated'));
  };

  const postJSON = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || T.strings.error || 'Error');
      err.data = data;
      throw err;
    }
    return data;
  };

  const announce = (msg) => {
    const live = document.getElementById('ts-cart-live');
    if (live) {
      live.textContent = '';
      window.setTimeout(() => (live.textContent = msg), 60);
    }
  };

  /* Free-shipping progress: threshold is stored in the shop currency and
     converted with Shopify's presentment rate so it works in USD and GBP. */
  const updateShippingBars = () => {
    document.querySelectorAll('[data-ts-ship-bar]').forEach((wrap) => {
      const base = Number(wrap.dataset.threshold || 0);
      const total = Number(wrap.dataset.total || 0);
      const rate = Number(window.Shopify?.currency?.rate || 1);
      const goal = Math.round(base * 100 * rate);
      if (!goal) return;
      const left = Math.max(goal - total, 0);
      const pct = Math.min(100, Math.round((total / goal) * 100));
      wrap.style.setProperty('--ts-progress', `${pct}%`);
      const text = wrap.querySelector('[data-ts-ship-text]');
      if (text) {
        text.textContent = left > 0
          ? (wrap.dataset.msgAway || '').replace('[amount]', formatMoney(left))
          : wrap.dataset.msgDone || '';
      }
      const goalEl = wrap.querySelector('[data-ts-ship-goal]');
      if (goalEl) goalEl.textContent = formatMoney(goal);
    });
  };

  /* ---------- <cart-drawer> ---------- */
  const Base = window.TsDrawer || HTMLElement;
  class CartDrawer extends Base {
    connectedCallback() {
      super.connectedCallback && super.connectedCallback();
      this.bindCartEvents(this);
      updateShippingBars();
      document.addEventListener('click', (e) => {
        const link = e.target.closest('#cart-icon-bubble');
        if (!link) return;
        e.preventDefault();
        this.open(link);
      });
    }
    setActiveElement(el) {
      this.activeElement = el;
    }
    getSectionsToRender() {
      return sectionsForPage().map((id) => ({ id }));
    }
    renderContents(parsedState) {
      renderSections(parsedState.sections);
      // PDP add (Dawn product-form): show "Added ✓" on the main and sticky buttons for 600ms, then open.
      const btns = [...document.querySelectorAll('[data-ts-atc], [data-ts-sticky-submit]')];
      btns.forEach((b) => setBtnState(b, 'added'));
      announce(T.strings.added || '');
      window.setTimeout(() => {
        btns.forEach((b) => setBtnState(b, null));
        this.open(this.activeElement);
      }, ADDED_MS);
    }
    bindCartEvents(root) {
      bindCart(root);
    }
  }
  const drawer = () => document.querySelector('cart-drawer');
  const openDrawer = (trigger) => {
    const d = drawer();
    if (d && typeof d.open === 'function' && !document.querySelector('[data-ts-cart-page]')) d.open(trigger);
  };

  /* ---------- Line items ---------- */
  const changeLine = async (container, line, quantity) => {
    const row = container.querySelector(`[data-ts-line="${line}"]`);
    row?.classList.add('is-updating');
    try {
      const data = await postJSON(`${window.routes?.cart_change_url || '/cart/change'}.js`.replace('.js.js', '.js'), {
        line,
        quantity,
        sections: sectionsForPage(),
        sections_url: window.location.pathname,
      });
      renderSections(data.sections);
      announce(T.strings.updated || '');
    } catch (err) {
      row?.classList.remove('is-updating');
      const box = row?.querySelector('[data-ts-line-error]');
      if (box) {
        box.textContent = err.message;
        box.hidden = false;
      }
    }
  };

  const switchToCombo = async (btn) => {
    const combo = Number(btn.dataset.combo);
    const books = (btn.dataset.books || '').split(',').map(Number).filter(Boolean);
    const qtys = JSON.parse(btn.dataset.qtys || '{}');
    if (!combo || !books.length) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      const present = books.filter((id) => (qtys[id] || 0) > 0);
      let swap = 1;
      if (present.length === books.length) swap = Math.min(...present.map((id) => qtys[id]));
      const updates = {};
      present.forEach((id) => (updates[id] = Math.max(0, (qtys[id] || 0) - swap)));
      await postJSON('/cart/update.js', { updates });
      const data = await postJSON('/cart/add.js', {
        items: [{ id: combo, quantity: swap }],
        sections: sectionsForPage(),
        sections_url: window.location.pathname,
      });
      renderSections(data.sections);
      announce(btn.dataset.done || '');
    } catch (err) {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      const box = btn.closest('.ts-upsell')?.querySelector('[data-ts-upsell-error]');
      if (box) {
        box.textContent = err.message;
        box.hidden = false;
      }
    }
  };

  const bindCart = (root) => {
    if (root._tsCartBound) return;
    root._tsCartBound = true;
    root.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-ts-qty-minus]');
      const plus = e.target.closest('[data-ts-qty-plus]');
      const remove = e.target.closest('[data-ts-remove]');
      const sw = e.target.closest('[data-ts-switch-combo]');
      if (sw) {
        e.preventDefault();
        switchToCombo(sw);
        return;
      }
      if (remove) {
        e.preventDefault();
        changeLine(root, Number(remove.dataset.tsRemove), 0);
        return;
      }
      if (minus || plus) {
        const wrap = (minus || plus).closest('[data-ts-qty]');
        const input = wrap.querySelector('input');
        const line = Number(wrap.dataset.tsQty);
        const current = Number(input.value) || 0;
        const step = Number(input.step) || 1;
        const next = minus ? Math.max(0, current - step) : current + step;
        input.value = next;
        clearTimeout(wrap._t);
        wrap._t = setTimeout(() => changeLine(root, line, next), 250);
      }
    });
    root.addEventListener('change', (e) => {
      const input = e.target.closest('[data-ts-qty] input');
      if (!input) return;
      const wrap = input.closest('[data-ts-qty]');
      changeLine(root, Number(wrap.dataset.tsQty), Math.max(0, Number(input.value) || 0));
    });
  };

  /* ---------- Add-to-cart forms on cards / upsells ---------- */
  const setBtnState = (btn, state) => {
    if (!btn) return;
    btn.classList.remove('is-loading', 'is-added');
    btn.removeAttribute('aria-busy');
    if (state === 'loading') {
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    }
    if (state === 'added') {
      // "Added ✓" overlay (centred, button size unchanged); removed by the caller after 600ms.
      let done = btn.querySelector('.ts-btn__done');
      if (!done) { done = document.createElement('span'); done.className = 'ts-btn__done'; done.setAttribute('aria-hidden', 'true'); btn.appendChild(done); }
      done.innerHTML = `<span>${T.strings.added || 'Added'}</span><span class="ts-btn__tick">✓</span>`;
      btn.classList.add('is-added');
    }
  };
  const ADDED_MS = 600;
  const pause = (ms) => new Promise((r) => window.setTimeout(r, ms));
  window.tsSetBtnState = setBtnState;

  const addItems = async (items, btn) => {
    setBtnState(btn, 'loading');
    try {
      const data = await postJSON('/cart/add.js', {
        items,
        sections: sectionsForPage(),
        sections_url: window.location.pathname,
      });
      renderSections(data.sections);
      setBtnState(btn, 'added');
      announce(T.strings.added || '');
      await pause(ADDED_MS);
      openDrawer(btn);
      setBtnState(btn, null);
      return true;
    } catch (err) {
      setBtnState(btn, null);
      window.alert(err.message);
      return false;
    }
  };
  window.tsAddItems = addItems;

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-ts-add]');
    if (!form) return;
    e.preventDefault();
    const fd = new FormData(form);
    addItems([{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }], form.querySelector('[type="submit"]'));
  });

  /* ---------- Quick add (products with several variants) ---------- */
  const quickAdd = async (btn) => {
    const handle = btn.dataset.tsQuickAdd;
    setBtnState(btn, 'loading');
    let product;
    try {
      const res = await fetch(`${(T.routes && T.routes.root) || '/'}products/${handle}.js`.replace('//products', '/products'));
      product = await res.json();
    } catch (err) {
      setBtnState(btn, null);
      return;
    }
    setBtnState(btn, null);
    let dlg = document.getElementById('ts-quick-add');
    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.id = 'ts-quick-add';
      dlg.className = 'ts-modal ts-quick';
      document.body.appendChild(dlg);
    }
    const first = product.variants.find((v) => v.available) || product.variants[0];
    const img = (first.featured_image && first.featured_image.src) || product.featured_image;
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    dlg.innerHTML = `
      <div class="ts-modal__inner">
        <button type="button" class="ts-iconbtn ts-modal__close" data-ts-dialog-close aria-label="${esc(T.strings.close || 'Close')}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19"/></svg>
        </button>
        <div class="ts-quick__grid">
          <div class="ts-quick__media">${img ? `<img src="${esc(img)}${img.includes('?') ? '&' : '?'}width=600" alt="" width="600" height="600" loading="lazy">` : ''}</div>
          <div class="ts-quick__info">
            <h2 class="ts-h3" id="ts-quick-title">${esc(product.title)}</h2>
            <p class="ts-quick__price" data-ts-q-price></p>
            <form data-ts-quick-form>
              ${product.options
                .map(
                  (opt, i) => `
                <label class="ts-quick__label" for="ts-q-opt-${i}">${esc(opt.name)}</label>
                <select class="ts-select" id="ts-q-opt-${i}" data-index="${i}">
                  ${opt.values.map((val) => `<option value="${esc(val)}"${first.options[i] === val ? ' selected' : ''}>${esc(val)}</option>`).join('')}
                </select>`
                )
                .join('')}
              <button type="submit" class="ts-btn ts-btn--block" data-ts-q-submit>
                <span class="ts-btn__label">${esc(T.strings.addToCart || 'Add to cart')}</span>
                <span class="ts-btn__spinner" aria-hidden="true"><span class="ts-spinner"></span></span>
              </button>
              <a class="ts-quick__details" href="${esc(product.url)}">${esc(T.strings.viewDetails || 'View full details')}</a>
            </form>
          </div>
        </div>
      </div>`;
    dlg.setAttribute('aria-labelledby', 'ts-quick-title');
    const form = dlg.querySelector('[data-ts-quick-form]');
    const priceEl = dlg.querySelector('[data-ts-q-price]');
    const submit = dlg.querySelector('[data-ts-q-submit]');
    const imgEl = dlg.querySelector('.ts-quick__media img');
    const current = () => {
      const sel = [...form.querySelectorAll('select')].map((s) => s.value);
      return product.variants.find((v) => v.options.every((o, i) => o === sel[i]));
    };
    const refresh = () => {
      const v = current();
      if (!v) {
        priceEl.textContent = T.strings.unavailable || 'Unavailable';
        submit.disabled = true;
        return;
      }
      priceEl.innerHTML = v.compare_at_price > v.price
        ? `<s class="ts-price__was">${formatMoney(v.compare_at_price)}</s> <span class="ts-price__now">${formatMoney(v.price)}</span>`
        : `<span class="ts-price__now">${formatMoney(v.price)}</span>`;
      submit.disabled = !v.available;
      submit.querySelector('.ts-btn__label').textContent = v.available ? T.strings.addToCart || 'Add to cart' : T.strings.soldOut || 'Sold out';
      if (imgEl && v.featured_image) imgEl.src = `${v.featured_image.src}${v.featured_image.src.includes('?') ? '&' : '?'}width=600`;
    };
    form.addEventListener('change', refresh);
    form.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const v = current();
      if (!v) return;
      const ok = await addItems([{ id: v.id, quantity: 1 }], submit);
      if (ok) dlg.close();
    });
    refresh();
    dlg.showModal();
    dlg.querySelector('select, [data-ts-q-submit]')?.focus();
    dlg.addEventListener('close', () => btn.focus(), { once: true });
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ts-quick-add]');
    if (!btn) return;
    e.preventDefault();
    quickAdd(btn);
  });

  /* Define last: the drawer is already in the DOM, so connectedCallback
     runs immediately and needs every helper above to exist. */
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
  document.querySelectorAll('[data-ts-cart-page]').forEach(bindCart);
  document.addEventListener('DOMContentLoaded', updateShippingBars);
})();
